import { useState } from 'react'
import { Image, Zap, Loader, Download, RefreshCw, ChevronDown } from 'lucide-react'
import useStore from '../store/useStore'
import { buildFluxWorkflow, buildFluxSchnellWorkflow, estimateCost, submitJob, pollJob, parseImageOutput } from '../api/runpod'

const STYLES = ['Claymation', 'Animated', 'Realistic', 'Illustrated', 'Cinematic', 'Product Shot']
const QUANTITIES = [1, 2, 4, 6, 10, 20]
const MODELS = ['FLUX.1 Dev', 'FLUX.1 Schnell']

export default function QuickImageGenerator() {
  const { characters, addJob, updateJob, completeJob, failJob, addImage, addNotification } = useStore()

  const [prompt, setPrompt] = useState('')
  const [negativePrompt, setNegativePrompt] = useState('')
  const [selectedCharacter, setSelectedCharacter] = useState('')
  const [selectedStyle, setSelectedStyle] = useState('Claymation')
  const [selectedModel, setSelectedModel] = useState('FLUX.1 Dev')
  const [quantity, setQuantity] = useState(1)
  const [generating, setGenerating] = useState(false)
  const [results, setResults] = useState([])
  const [selectedImages, setSelectedImages] = useState(new Set())

  const character = characters.find(c => c.id === selectedCharacter)
  const cost = estimateCost('flux_image', { quantity })

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      addNotification({ type: 'error', message: 'Enter a scene description first.' })
      return
    }

    const fullPrompt = [
      character?.trigger_word,
      selectedStyle.toLowerCase(),
      prompt.trim(),
    ].filter(Boolean).join(', ')

    const jobId = `img_${Date.now()}`
    setGenerating(true)
    setResults([])
    setSelectedImages(new Set())
    addJob({ id: jobId, label: `${quantity} images — ${selectedModel}`, type: 'flux_image', status: 'IN_QUEUE' })

    try {
      const allImages = []

      for (let i = 0; i < quantity; i++) {
        const workflow = selectedModel === 'FLUX.1 Schnell'
          ? buildFluxSchnellWorkflow({ prompt: fullPrompt })
          : buildFluxWorkflow({ prompt: fullPrompt, steps: 20, guidance: 3.5 })

        updateJob(jobId, { status: `IN_PROGRESS (${i + 1}/${quantity})` })
        const { id: runpodJobId } = await submitJob(workflow)
        const output = await pollJob(runpodJobId, (status) => updateJob(jobId, { status }))

        // Parse base64 images from ComfyUI worker output
        const dataUrls = parseImageOutput(output)
        allImages.push(...dataUrls)
      }

      const imageObjects = allImages.map((dataUrl, i) => ({
        id: `img_${Date.now()}_${i}`,
        url: dataUrl,
        prompt: fullPrompt,
        model: selectedModel,
        character: character?.name || 'None',
        createdAt: Date.now(),
      }))

      imageObjects.forEach(img => addImage(img))
      setResults(imageObjects)
      completeJob(jobId, { output: { images: allImages } })
      addNotification({ type: 'success', message: `Generated ${allImages.length} image(s).` })
    } catch (err) {
      failJob(jobId, err.message)
      addNotification({ type: 'error', message: `Generation failed: ${err.message}` })
    } finally {
      setGenerating(false)
    }
  }

  const toggleSelect = (id) => {
    setSelectedImages(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const downloadSelected = () => {
    results.filter(img => selectedImages.has(img.id)).forEach(img => {
      const a = document.createElement('a')
      a.href = img.url
      a.download = `aiq-image-${img.id}.png`
      a.click()
    })
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Image size={22} className="text-blue-400" />
          Quick Image Generator
        </h2>
        <p className="text-slate-400 text-sm mt-1">Generate static images fast. No canvas needed.</p>
      </div>

      <div className="grid grid-cols-3 gap-6">

        {/* Left — Controls */}
        <div className="col-span-1 space-y-4">

          {/* Character */}
          {characters.length > 0 && (
            <div className="card p-4">
              <label className="label">Character LoRA</label>
              <select
                value={selectedCharacter}
                onChange={e => setSelectedCharacter(e.target.value)}
                className="input text-sm"
              >
                <option value="">No character</option>
                {characters.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {character && (
                <p className="text-xs text-brand-400 mt-1 font-mono">{character.trigger_word}</p>
              )}
            </div>
          )}

          {/* Model + Style */}
          <div className="card p-4 space-y-3">
            <div>
              <label className="label">Model</label>
              <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} className="input text-sm">
                {MODELS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Style</label>
              <div className="grid grid-cols-2 gap-1.5">
                {STYLES.map(s => (
                  <button
                    key={s}
                    onClick={() => setSelectedStyle(s)}
                    className={`py-1.5 text-xs rounded-lg border transition-colors ${
                      selectedStyle === s
                        ? 'bg-brand-500 border-brand-400 text-white'
                        : 'bg-surface border-surface-border text-slate-400 hover:border-brand-500/50'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Prompt */}
          <div className="card p-4 space-y-3">
            <div>
              <label className="label">Scene Description</label>
              <textarea
                rows={4}
                className="input text-sm resize-none"
                placeholder="Character standing at a whiteboard explaining tax strategy, bright office, confident expression..."
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Negative Prompt (optional)</label>
              <input
                type="text"
                className="input text-sm"
                placeholder="blurry, low quality, distorted..."
                value={negativePrompt}
                onChange={e => setNegativePrompt(e.target.value)}
              />
            </div>
          </div>

          {/* Quantity */}
          <div className="card p-4">
            <label className="label">Quantity</label>
            <div className="flex gap-2 flex-wrap">
              {QUANTITIES.map(q => (
                <button
                  key={q}
                  onClick={() => setQuantity(q)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    quantity === q
                      ? 'bg-brand-500 border-brand-400 text-white'
                      : 'bg-surface border-surface-border text-slate-400 hover:border-brand-500/50'
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
            {cost && (
              <p className="text-xs text-green-400 mt-2">
                Est. cost: {cost.estimatedCost} ({quantity} × {cost.costPerUnit})
              </p>
            )}
          </div>

          {/* Generate */}
          <button
            onClick={handleGenerate}
            disabled={generating || !prompt.trim()}
            className="btn-primary w-full justify-center py-3 text-base"
          >
            {generating ? (
              <><Loader size={18} className="generation-spinner" /> Generating {quantity} images...</>
            ) : (
              <><Zap size={18} /> Generate {quantity} Image{quantity > 1 ? 's' : ''}</>
            )}
          </button>

        </div>

        {/* Right — Results */}
        <div className="col-span-2">
          {results.length === 0 ? (
            <div className="card h-full flex flex-col items-center justify-center p-12 text-center">
              <Image size={56} className="text-slate-700 mb-4" />
              <h3 className="text-white font-medium mb-2">Results appear here</h3>
              <p className="text-slate-500 text-sm">Configure your settings and hit Generate.</p>
              {generating && (
                <div className="mt-6">
                  <Loader size={24} className="text-brand-400 generation-spinner mx-auto mb-2" />
                  <p className="text-brand-300 text-sm">Generating on your A40 GPU...</p>
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* Toolbar */}
              {selectedImages.size > 0 && (
                <div className="flex items-center gap-3 mb-3 px-1">
                  <span className="text-sm text-slate-400">{selectedImages.size} selected</span>
                  <button onClick={downloadSelected} className="btn-primary text-sm py-1.5">
                    <Download size={14} /> Download Selected
                  </button>
                  <button onClick={() => setSelectedImages(new Set())} className="btn-ghost text-sm py-1.5">
                    Clear
                  </button>
                </div>
              )}

              {/* Image grid */}
              <div className="grid grid-cols-2 gap-3">
                {results.map((img, i) => (
                  <div
                    key={img.id}
                    onClick={() => toggleSelect(img.id)}
                    className={`relative rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${
                      selectedImages.has(img.id)
                        ? 'border-brand-400 shadow-glow-brand'
                        : 'border-transparent hover:border-surface-muted'
                    }`}
                  >
                    {img.url ? (
                      <img src={img.url} alt={`Result ${i + 1}`} className="w-full object-cover aspect-square" />
                    ) : (
                      <div className="w-full aspect-square bg-surface-muted flex items-center justify-center">
                        <Image size={32} className="text-slate-600" />
                      </div>
                    )}
                    {selectedImages.has(img.id) && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-3 py-1.5 text-xs text-slate-300">
                      #{i + 1} · {img.model}
                    </div>
                  </div>
                ))}
              </div>

              {/* Regenerate */}
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="btn-secondary w-full justify-center mt-4"
              >
                <RefreshCw size={14} /> Generate Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
